import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import BouncingDotsLoader from './utils/BouncingDotsLoader';
import '../assets/css/typing.css';
import ChatMessageNotificationText from './ChatMessageNotificationText';

const useStyles = makeStyles(_ => ({
	messageContainer: {
		textAlign: 'left',
		display: 'flex',
		flexFlow: 'column wrap'
	},
	agentMessageContainer: {
		alignItems: 'flex-start',
		alignContent: 'flex-start'
	},
	userMessageContainer: {
		alignItems: 'flex-end',
		alignContent: 'flex-end'
	},
	agentMessage: {
		color: '#B59048',
		marginLeft: '7px'
	},
	userMessage: {
		color: '#3F2A1C',
		marginRight: '7px'
	},
	chatText: {
		fontFamily: 'Inter',
		fontStyle: 'normal',
		fontWeight: '400'
	},
	messageSender: {
		height: '14px',
		fontSize: '10px',
		lineHeight: '130%',
		marginBottom: '5px'
	},
	bubbleContainer: {
		display: 'flex',
		position: 'relative',
		alignItems: 'center'
	},
	messageBubble: {
		maxWidth: '268px',
		minWidth: '10px',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		padding: '1px 12px',
		borderRadius: '12px',
		order: '0',
		alignSelf: 'stretch',
		flexGrow: '1'
	},
	agentMessageBubble: {
		background: '#F1E9DA'
	},
	userMessageBubble: {
		background: '#A6823C'
	},

	message: {
		fontSize: '15px',
		lineHeight: 'normal',
		letterSpacing: '-0.408px',
		fontFeatureSettings: '\'case\' on',
		color: '#FFFFFF',
		wordBreak: 'break-word',
		whiteSpace: 'pre-line'
		// hyphens: 'auto'
	},

	businessMessage: {
		color: '#000000',
		fontSize: '15px',
		lineHeight: '24px',
		fontWeight: 450 /* Regular */,

		'& > p': {
			margin: 0
		},
		'& > ol': {
			margin: '0.4em 2 -0.625em',
			paddingLeft: '1.5em',
			lineHeight: '1.25em'
		},
		'& > h1': {
			fontSize: '20px',
			lineHeight: '32px',
			fontWeight: 600
		},
		'& > h2, & > h3, & > h4, & > h5, & > h6': {
			fontSize: '18px',
			lineHeight: '28px',
			fontWeight: 600,
			margin: '5px',
			paddingBottom: '4px'
		}
	},

	messageTime: {
		height: '16px',
		fontSize: '12px',
		lineHeight: '130%',
		color: '#7A869A',
		marginTop: '4px',
		marginBottom: '4px'
	},
	agentMessageTime: {
		marginLeft: '7px',
		visibility: 'hidden',

		'&:hover': {
			visibility: 'visible'
		}
	},
	userMessageTime: {
		marginRight: '7px',
		visibility: 'hidden',

		'&:hover': {
			visibility: 'visible'
		}
	},
	marginBottom: {
		marginBottom: '8px'
	},
	imageFile: {
		width: '100%',
		maxWidth: '200px',
		maxHeight: '200px',
		objectFit: 'contain',
		marginLeft: 'auto',
		borderRadius: '8px'
	},
	userFileUpd: {
		display: 'flex',
		flexDirection: 'column',
		paddingTop: '10px'
	}
}));

const MESSAGE_VISIBLE_TIME = 500;
const ANIMATION_TIME = 1000;
const MESSAGE_TIMEOUT_TIME = ANIMATION_TIME + MESSAGE_VISIBLE_TIME;
const MESSAGE_QUERY_INTERVAL = 100;

export default function ChatMessageTyping({ companyConfig, notifications, setNotifications, isAutopilot }) {
	const classes = useStyles();
	const { t } = useTranslation();
	const DEFAULT_MESSAGE = t('chatMessageTyping.thinking');

	const interval = useRef(null);
	const timeout = useRef(null);

	const [currentMessage, setCurrentMessage] = useState(DEFAULT_MESSAGE);
	const [messageQueue, setMessageQueue] = useState([]);
	const [shouldDisplayCurrent, setShouldDisplayCurrent] = useState(true);

	useEffect(() => {
		if (notifications.length > 0) {
			setMessageQueue(prev => [...prev, ...notifications]);
			setNotifications([]);
		}
	}, [notifications, setNotifications]);

	useEffect(() => {
		interval.current = setInterval(() => {
			if (messageQueue.length > 0 && !shouldDisplayCurrent) {
				const firstMessage = messageQueue[0];
				setCurrentMessage(firstMessage);
				setMessageQueue(prev => prev.slice(1));
				setShouldDisplayCurrent(true);

				timeout.current = setTimeout(() => {
					setShouldDisplayCurrent(false);
				}, MESSAGE_TIMEOUT_TIME);
			}
		}, MESSAGE_QUERY_INTERVAL);

		return () => {
			if (interval.current !== null) {
				clearInterval(interval.current);
			}
		};
	}, [messageQueue, shouldDisplayCurrent]);

	useEffect(() => {
		setTimeout(() => {
			setShouldDisplayCurrent(false);
		}, MESSAGE_TIMEOUT_TIME);

		return () => {
			if (timeout.current !== null) {
				clearTimeout(timeout.current);
			}
		};
	}, []);

	return (
		<div className={classes.bubbleContainer} tabIndex={0}>
			<span style={{ alignSelf: 'flex-start' }}>
				<span className="visually-hidden">{t('agentResponse')}</span>
				<img
					src={companyConfig.botIconUrl ? companyConfig.botIconUrl : companyConfig.chatLogo}
					alt=""
					style={{
						maxWidth: '36px',
						maxHeight: '36px',
						overflow: 'hidden',
						borderRadius: '36px',
						marginRight: '10px',
						alignSelf: 'flex-start'
					}}
				/>
			</span>
			{isAutopilot ? (
				<ChatMessageNotificationText message={currentMessage} />
			) : (
				<BouncingDotsLoader />
			)}
		</div>
	);
}
